import { useEffect, useState, type ReactNode } from "react";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  Link,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api, setOnAuthExpired } from "@/lib/api";
import { FacilityNotSetUpPage } from "@/components/FacilityNotSetUpPage";
import { LoginPage } from "@/components/LoginPage";
import { NoAccessPage } from "@/components/NoAccessPage";
import { PracticeCombobox } from "@/components/PracticeCombobox";
import { ProfileBadge } from "@/components/ProfileBadge";
import { useAuth } from "@/hooks/useAuth";
import {
  PracticeProvider,
  usePracticeContext,
} from "@/hooks/usePracticeContext";
import { Practices } from "@/pages/Practices";
import {
  PracticeDetailLayout,
  PracticeFacilityPage,
  PracticeAgentPage,
  PracticeHoursPage,
  PracticeMessagesPage,
  PracticeOutOfOfficePage,
  PracticeRequestTypesPage,
} from "@/pages/PracticeSettings";
import { PracticeSummaryPage } from "@/pages/PracticeSummary";
import { Calls } from "@/pages/Calls";
import { CallDetail } from "@/pages/CallDetail";
import { TestPanel } from "@/pages/TestPanel";
import { AdminSystemPrompts } from "@/pages/AdminSystemPrompts";
import {
  UnprocessedEvents,
  useOpenUnprocessedEventsCount,
} from "@/pages/UnprocessedEvents";
import { type AppUser, type MeInfo } from "@/lib/types";

export const App = () => {
  const { user, checking, isAuthenticated, onSignedIn, logout } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    setOnAuthExpired(() => {
      setSessionExpired(true);
      logout();
    });
  }, [logout]);

  if (checking) return null;

  if (!isAuthenticated || !user) {
    return (
      <LoginPage
        onLogin={(u) => {
          setSessionExpired(false);
          onSignedIn(u);
        }}
        message={
          sessionExpired
            ? "Vaše přihlášení vypršelo — přihlaste se prosím znovu."
            : undefined
        }
      />
    );
  }

  return <AuthenticatedApp user={user} onSignOut={logout} />;
};

/** Post-auth root. Fetches `GET /api/me` once to learn the user's pool kind, then branches into the admin UI
 *  (AdminUser) or the practice-scoped UI (DoctorUser).
 */
const AuthenticatedApp = ({
  user,
  onSignOut,
}: {
  user: AppUser;
  onSignOut: () => void;
}) => {
  const meQuery = useQuery<MeInfo>({
    queryKey: ["me"],
    queryFn: api.me,
    staleTime: 60_000,
  });

  // Same build serves both admin.hlasemmy.cz and hlasemmy.cz — switch the browser tab title once we know the pool.
  useEffect(() => {
    const kind = meQuery.data?.kind;
    if (!kind) return;
    document.title = kind === "admin" ? "Hlas emmy - admin" : "Hlas emmy";
  }, [meQuery.data?.kind]);

  if (meQuery.isPending) return null;
  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md rounded-lg border border-red-200 bg-white p-6 text-center text-sm text-red-700 shadow-sm">
          Nepodařilo se načíst profil (
          {meQuery.error?.message ?? "neznámá chyba"}). Zkuste stránku znovu
          načíst.
        </div>
      </div>
    );
  }

  if (meQuery.data.kind === "doctor") {
    return (
      <PracticeProvider
        accessibleFacilityIds={meQuery.data.accessibleFacilityIds}
      >
        <DoctorApp user={user} onSignOut={onSignOut} />
      </PracticeProvider>
    );
  }

  return <AdminApp user={user} onSignOut={onSignOut} />;
};

/** Existing Stage 1 admin UI — every practice visible, all admin surfaces enabled. */
const AdminApp = ({
  user,
  onSignOut,
}: {
  user: AppUser;
  onSignOut: () => void;
}) => (
  <div className="min-h-screen bg-gray-100">
    <header className="bg-[#1a2b6b] text-white shadow-md">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-2.5">
        <div className="flex items-center gap-6">
          <span className="text-xl tracking-tight text-white">
            <span className="font-bold">hlas</span>
            <span className="italic font-light">emmy</span>
          </span>
          <Link to="/practices" className="text-sm text-white hover:opacity-80">
            Ordinace
          </Link>
          <Link
            to="/admin/system-prompts"
            className="text-sm text-white hover:opacity-80"
          >
            Správa hlášek
          </Link>
          <Link to="/calls" className="text-sm text-white hover:opacity-80">
            Hovory
          </Link>
          <UnprocessedEventsLink />
        </div>
        <ProfileBadge user={user} onSignOut={onSignOut} />
      </div>
    </header>
    <Routes>
      <Route path="/" element={<Navigate to="/practices" replace />} />
      <Route path="/practices" element={<Practices />} />
      <Route path="/practices/:id" element={<PracticeDetailLayout />}>
        <Route index element={<Navigate to="summary" replace />} />
        <Route path="summary" element={<PracticeSummaryPage />} />
        <Route path="facility" element={<PracticeFacilityPage />} />
        <Route path="messages" element={<PracticeMessagesPage />} />
        <Route path="request-types" element={<PracticeRequestTypesPage />} />
        <Route path="hours" element={<PracticeHoursPage />} />
        <Route path="agent" element={<PracticeAgentPage />} />
        <Route path="out-of-office" element={<PracticeOutOfOfficePage />} />
      </Route>
      <Route path="/calls" element={<Calls />} />
      <Route path="/calls/:id" element={<CallDetail />} />
      <Route path="/test" element={<TestPanel />} />
      <Route path="/admin/system-prompts" element={<AdminSystemPrompts />} />
      <Route path="/admin/unprocessed-events" element={<UnprocessedEvents />} />
    </Routes>
  </div>
);

const UnprocessedEventsLink = () => {
  const openCount = useOpenUnprocessedEventsCount();
  const open = openCount.data?.total ?? 0;
  return (
    <Link
      to="/admin/unprocessed-events"
      className="text-sm text-white hover:opacity-80"
    >
      Nezpracované události
      {open > 0 && <span className="ml-1 font-semibold">({open})</span>}
    </Link>
  );
};

/** Stage 2 practice-staff UI. Routes are scoped to the active option (combobox-driven). When the user has no
 *  accessible facilities at all, renders `<NoAccessPage />` in full — only sign-out is reachable. When the active
 *  option is an unconfigured facility, the normal chrome + combobox stay available; the main pane shows the
 *  per-facility "not set up" page.
 */
const DoctorApp = ({
  user,
  onSignOut,
}: {
  user: AppUser;
  onSignOut: () => void;
}) => {
  const { options, activeOption, activePractice, isLoading, error } =
    usePracticeContext();

  if (isLoading) return null;
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md rounded-lg border border-red-200 bg-white p-6 text-center text-sm text-red-700 shadow-sm">
          Nepodařilo se načíst seznam ordinací ({error.message}). Zkuste stránku
          znovu načíst.
        </div>
      </div>
    );
  }
  if (options.length === 0 || !activeOption) {
    return <NoAccessPage user={user} onSignOut={onSignOut} />;
  }

  // For a configured active practice the landing tab is Souhrn — the page that explains what the assistant does before
  // it offers anything to change. For an unconfigured facility the user bounces to the contact-support page.
  const rootRedirect =
    activeOption.kind === "configured"
      ? `/practices/${activeOption.practice.id}/summary`
      : `/facility-not-set-up/${activeOption.facilityId}`;

  // One-tier nav (collapsed from the previous two-tier layout). The practice-detail layout doesn't render its own
  // tab bar for doctors — these links are the nav.
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-[#1a2b6b] text-white shadow-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-2.5">
          <div className="flex items-center gap-6">
            <span className="text-xl tracking-tight text-white">
              <span className="font-bold">hlas</span>
              <span className="italic font-light">emmy</span>
            </span>
            {activePractice && (
              <>
                <DoctorNavLink to={`/practices/${activePractice.id}/summary`}>
                  Souhrn
                </DoctorNavLink>
                <DoctorNavLink to={`/practices/${activePractice.id}/messages`}>
                  Hlášky
                </DoctorNavLink>
                <DoctorNavLink
                  to={`/practices/${activePractice.id}/request-types`}
                >
                  Typy požadavků
                </DoctorNavLink>
                <DoctorNavLink to={`/practices/${activePractice.id}/hours`}>
                  Ordinační hodiny
                </DoctorNavLink>
                <DoctorNavLink
                  to={`/practices/${activePractice.id}/out-of-office`}
                >
                  Dovolená
                </DoctorNavLink>
                <DoctorNavLink to="/calls">Hovory</DoctorNavLink>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <PracticeCombobox />
            <ProfileBadge user={user} onSignOut={onSignOut} />
          </div>
        </div>
      </header>
      <Routes>
        <Route path="/" element={<Navigate to={rootRedirect} replace />} />
        <Route path="/practices/:id" element={<PracticeDetailLayout />}>
          <Route index element={<Navigate to="summary" replace />} />
          <Route path="summary" element={<PracticeSummaryPage />} />
          <Route path="messages" element={<PracticeMessagesPage />} />
          <Route path="request-types" element={<PracticeRequestTypesPage />} />
          <Route path="hours" element={<PracticeHoursPage />} />
          <Route path="out-of-office" element={<PracticeOutOfOfficePage />} />
          {/* No `facility` route for doctor — admin-only general section + Emmy wiring had no practice-user fields
              after the earlier hide-for-doctor pass.

              No `agent` route either. Persona, rules, optional tools and per-type permissions are the assistant's
              behaviour expressed as configuration, and a practice reads that as prose on Souhrn and changes it by
              asking. The tab stays for admin, where the technical form is the right tool. */}
          {/* A tab a doctor cannot reach lands here rather than on the layout with an empty outlet, which renders as a
              blank page under working chrome. `/agent` is the live case: it was in the nav until this change, so it is
              in bookmarks and in open tabs. */}
          <Route path="*" element={<Navigate to="../summary" replace />} />
        </Route>
        <Route path="/calls" element={<Calls />} />
        <Route path="/calls/:id" element={<CallDetail />} />
        <Route
          path="/facility-not-set-up/:facilityId"
          element={<FacilityNotSetUpRoute />}
        />
        <Route path="*" element={<Navigate to={rootRedirect} replace />} />
      </Routes>
    </div>
  );
};

const DoctorNavLink = ({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      "text-sm transition-colors " +
      (isActive ? "font-medium text-white" : "text-white/70 hover:text-white")
    }
  >
    {children}
  </NavLink>
);

const FacilityNotSetUpRoute = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  if (!facilityId) return null;
  return <FacilityNotSetUpPage facilityId={facilityId} />;
};
